import { FC, ReactNode, useCallback, useMemo } from 'react';
import { Avatar, Button, Col, Divider, Drawer, Row, Tabs, Typography } from 'antd';
import { TbStar } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { usePlatformHotkeys as useHotkeys } from '@/hooks/usePlatformHotkeys';
import PreviousNextResourceButtonGroup from '@/shared/components/buttons/PreviousNextResourceButtonGroup';

export interface ListRecordDetailDrawerSocialLink {
    href?: string;
    icon: ReactNode;
    key: string;
}

export interface ListRecordDetailDrawerTab {
    children: ReactNode;
    key: string;
    label: ReactNode;
}

export interface ListRecordDetailDrawerProps {
    avatarFallback: ReactNode;
    avatarShape?: 'circle' | 'square';
    avatarSrc?: string;
    footer?: ReactNode;
    metadata: Record<string, any>;
    modals?: ReactNode;
    onOpenChange: (open: boolean) => void;
    open: boolean;
    records: any[];
    setMetadata: (metadata: Record<string, any>) => void;
    setSelectedRecord: (record: any) => void;
    socialLinks?: ListRecordDetailDrawerSocialLink[];
    tabs: ListRecordDetailDrawerTab[];
    title?: ReactNode;
}

const ListRecordDetailDrawer: FC<ListRecordDetailDrawerProps> = ({
    avatarFallback,
    avatarShape = 'circle',
    avatarSrc,
    footer,
    metadata,
    modals,
    onOpenChange,
    open,
    records,
    setMetadata,
    setSelectedRecord,
    socialLinks = [],
    tabs,
    title,
}) => {
    const { token } = useAntDesignTheme();
    const totalRows = records.length;
    const currentIndex = Number.isFinite(metadata?.row) ? metadata.row : 0;

    const selectRecordAtIndex = useCallback(
        (row: number) => {
            if (!totalRows) {
                return;
            }

            const nextRow = ((row % totalRows) + totalRows) % totalRows;

            setMetadata({ ...metadata, row: nextRow });
            setSelectedRecord(records[nextRow]);
        },
        [metadata, records, setMetadata, setSelectedRecord, totalRows],
    );

    const handleEntryUp = useCallback(() => selectRecordAtIndex(currentIndex - 1), [currentIndex, selectRecordAtIndex]);
    const handleEntryDown = useCallback(
        () => selectRecordAtIndex(currentIndex + 1),
        [currentIndex, selectRecordAtIndex],
    );

    useHotkeys('alt+arrowup', handleEntryUp);
    useHotkeys('alt+arrowdown', handleEntryDown);

    const tabItems = useMemo(
        () =>
            tabs.map((tab) => ({
                ...tab,
                children: (
                    <section
                        style={{
                            boxSizing: 'border-box',
                            height: '100%',
                            minHeight: 0,
                            overflow: 'auto',
                            paddingTop: '0.75rem',
                        }}
                    >
                        {tab.children}
                    </section>
                ),
            })),
        [tabs],
    );

    return (
        <Drawer
            destroyOnHidden
            mask
            maskClosable
            open={open}
            placement='right'
            width='40rem'
            onClose={() => onOpenChange(false)}
            styles={{
                body: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, padding: 0 },
                mask: { backgroundColor: 'transparent' },
                wrapper: { borderLeft: `1px solid ${token.colorBorder}`, maxWidth: '100%' },
            }}
            extra={
                <PreviousNextResourceButtonGroup
                    showCount
                    resources={records}
                    resource={records?.[currentIndex]}
                    currentIndex={currentIndex}
                    enableHotkeys={false}
                    onPreviousResource={handleEntryUp}
                    onNextResource={handleEntryDown}
                    singularResourceLabel='record'
                    pluralResourceLabel='records'
                />
            }
        >
            <div
                style={{
                    backgroundColor: token.colorBgBase,
                    borderBottom: `1px solid ${token.colorBorder}`,
                    flex: 'none',
                    height: '10rem',
                    position: 'sticky',
                }}
            />
            <Avatar
                shape={avatarShape}
                size={64}
                src={avatarSrc}
                style={{
                    backgroundColor: token.colorBgBase,
                    border: `0.125rem solid ${token.colorBorder}`,
                    color: token.colorText,
                    fontSize: '1.5rem',
                    marginLeft: '1.5rem',
                    position: 'absolute',
                    top: 184,
                }}
            >
                {avatarFallback}
            </Avatar>
            <Row style={{ flex: 'none', margin: '0.5rem 1.5rem' }}>
                <Col flex='auto' />
                <Col flex='none'>
                    <span style={{ color: token.colorTextTertiary }}>
                        {socialLinks
                            .filter((link) => Boolean(link.href))
                            .map((link, index) => (
                                <a
                                    key={link.key}
                                    href={link.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    style={{
                                        color: 'inherit',
                                        marginLeft: index === 0 ? 0 : '0.5rem',
                                    }}
                                >
                                    {link.icon}
                                </a>
                            ))}
                    </span>
                </Col>
            </Row>
            <div
                style={{
                    display: 'flex',
                    flex: '1 1 0%',
                    flexDirection: 'column',
                    margin: '1rem 1.5rem 0',
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <Row align='middle' style={{ marginBottom: '-0.5rem' }}>
                    <Col flex='none'>
                        <Typography.Title level={3} style={{ marginBottom: 0 }}>
                            {title}
                        </Typography.Title>
                    </Col>
                    <Col flex='auto'>
                        <Button size='small' style={{ marginLeft: '0.75rem', verticalAlign: '-0.375rem' }} type='text'>
                            <TbStar style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }} />
                        </Button>
                    </Col>
                </Row>
                <Tabs size='small' items={tabItems} style={{ flex: '1 1 0%', minHeight: 0 }} />
            </div>
            {footer && (
                <div style={{ flex: 'none', paddingBottom: '1.5rem', width: '100%' }}>
                    <Divider />
                    {footer}
                </div>
            )}
            {modals}
        </Drawer>
    );
};

export default ListRecordDetailDrawer;
